import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { EuropeMap } from '../components/EuropeMap';
import { 
  CreditCard, 
  Building, 
  Globe, 
  ShoppingCart, 
  Smartphone, 
  RefreshCw,
  Shield,
  Lock,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const iconMap = {
  CreditCard,
  Building,
  Globe,
  ShoppingCart,
  Smartphone,
  RefreshCw,
};

export const Services = () => {
  const { t, translations } = useLanguage();

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-display" data-testid="services-title">
            {t('services.hero.title')}
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-12">
            {t('services.hero.subtitle')}
          </p>

          {/* European Map Visual */}
          <div className="relative max-w-5xl mx-auto h-[400px] sm:h-[500px] mt-8" data-testid="europe-map-container">
            <EuropeMap />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {translations.services.cards.map((service, index) => {
              const IconComponent = iconMap[service.icon] || CreditCard;
              return (
                <div
                  key={index}
                  className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-emerald-500/50 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500/20 transition-colors">
                    <IconComponent className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 lg:py-24 bg-zinc-900/50" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
              {t('services.compliance.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {translations.services.compliance.items.map((item, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
                data-testid={`compliance-card-${index}`}
              >
                <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {index === 0 && <Shield className="w-8 h-8 text-amber-400" />}
                  {index === 1 && <Lock className="w-8 h-8 text-amber-400" />}
                  {index === 2 && <CheckCircle className="w-8 h-8 text-amber-400" />}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-zinc-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-display">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-zinc-400 mb-8">
            {t('cta.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/30"
            data-testid="services-cta-button"
          >
            {t('cta.button1')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
